import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import moment from 'moment';
import { useCallback, useEffect, useRef, useState } from 'react';
import { downloadFile } from 'utils/downloadFile';
import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { setReportDownloadUrl, setReportState, setReportStatus } from 'v3/data/store/Rank/RankSeller/actions';
import { useSse } from 'v3/hooks/useSse';
import { Content, LeftContainer, RightContainer } from 'v3/pages/SellersRanking/Header/SellerRankFilters/styles';
import { DateRange, FilterByWeekOrMonthAndPeriodProps } from 'v3/pages/SellersRanking/Header/SellerRankFilters/types';
import * as api from 'v3/services/api';
import { getSellerRankingReportData, getSellerRankingReportStreamUrl } from 'v3/services/api';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { AutorenewIconStyles } from 'v3/shared/components/Button/MiniButton/styles';
import { FilterByPeriod } from 'v3/shared/components/Filters/FilterByPeriod';
import { FilterByWeekOrMonth } from 'v3/shared/components/Filters/FilterByWeekOrMonth';
import { FeatureFlagEnum } from 'v3/shared/enums/feature-flag.enum';
import { WeekOrMonthTabsEnum } from 'v3/shared/enums/week-or-month-tabs.enum';

export const FilterByWeekOrMonthAndPeriod = ({ dateRange, setDateRange }: FilterByWeekOrMonthAndPeriodProps) => {
  const [selectedWeekOrMonth, setSelectedWeekOrMonth] = useState<WeekOrMonthTabsEnum | null>(
    WeekOrMonthTabsEnum.MonthTab,
  );

  const { filter, report } = useStoreSelector((store) => store.rankSeller);
  const [downloadButtonFeatureEnabled, setdownloadButtonFeatureEnabled] = useState(false);
  const downloadTriggeredRef = useRef(false);

  const LAST_REPORT_KEY = 'sellerRankingLastReport';

  const streamUrl = report.jobId ? getSellerRankingReportStreamUrl(report.jobId) : '';
  const { data: sseData, error: sseError, connect, disconnect, reset: sseReset } = useSse(streamUrl, 'COMPLETED');

  const handleChangePeriod = (newRange: DateRange) => {
    setDateRange(newRange);
    const [start, end] = newRange;
    if (start && end) {
      setSelectedWeekOrMonth(null);
    }
  };

  const handleChangeMonthOrWeek = (newSelected: WeekOrMonthTabsEnum) => {
    setSelectedWeekOrMonth(newSelected);
    setDateRange([null, null]);
  };

  const handleClearPeriod = () => {
    setSelectedWeekOrMonth(WeekOrMonthTabsEnum.MonthTab);
  };

  const handleGenerateReport = async () => {
    const today = moment();
    const reportFilter = {
      finalDate: filter.finalDate || today.endOf('month').toISOString(),
      startDate: filter.startDate || today.startOf('month').toISOString(),
    };

    downloadTriggeredRef.current = false;
    sseReset();

    try {
      const { data } = await getSellerRankingReportData(reportFilter);
      setReportState({
        jobId: data.jobId,
        status: 'PROCESSING',
        url: null,
        startDate: reportFilter.startDate,
        finalDate: reportFilter.finalDate,
      });
    } catch (error) {
      setReportStatus('FAILED');
      showErrorDialog('Erro ao iniciar a geração do relatório:', error);
    }
  };

  const fetchDownloadButtonFeatureFlag = useCallback(async () => {
    const response = await api.getFlagStatus(FeatureFlagEnum.IKCLOUD_22142_RANKING_SELLER_DOWNLOAD);

    if (response.data.enabled) {
      setdownloadButtonFeatureEnabled(true);
    }
  }, []);

  const getLastReportFromLocalStorage = useCallback(() => {
    const saved = localStorage.getItem(LAST_REPORT_KEY);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data.url) {
          setReportState(data);
        }
      } catch (e) {
        localStorage.removeItem(LAST_REPORT_KEY);
      }
    }
  }, []);

  useEffect(() => {
    fetchDownloadButtonFeatureFlag();
    getLastReportFromLocalStorage();
  }, [fetchDownloadButtonFeatureFlag, getLastReportFromLocalStorage]);

  const handleSseReceived = useCallback(() => {
    if (sseData?.status === 'COMPLETED' && !downloadTriggeredRef.current) {
      downloadTriggeredRef.current = true;
      setReportStatus('COMPLETED');
      setReportDownloadUrl(sseData.preSignedUrl);
      localStorage.setItem(
        LAST_REPORT_KEY,
        JSON.stringify({
          status: 'COMPLETED',
          jobId: report.jobId,
          url: sseData.preSignedUrl,
          startDate: report.startDate,
          finalDate: report.finalDate,
        }),
      );
      downloadFile(sseData.preSignedUrl);
      disconnect();
    }
  }, [sseData, disconnect, report.startDate, report.finalDate]);

  useEffect(() => {
    handleSseReceived();
  }, [handleSseReceived]);

  useEffect(() => {
    if (sseError || report?.status === 'FAILED') {
      setReportStatus('FAILED');
      showErrorDialog('Erro durante a geração do relatório:', sseError);
      disconnect();
    }
  }, [sseData, sseError, disconnect]);

  const isProcessing = report.status === 'PROCESSING';

  useEffect(() => {
    if (report.jobId && isProcessing) {
      connect();
    }
  }, [report.jobId, report.status, connect]);

  return (
    <Content>
      <LeftContainer></LeftContainer>

      <RightContainer>
        <FilterByPeriod dateRange={dateRange} setDateRange={handleChangePeriod} onClear={handleClearPeriod} />
        <FilterByWeekOrMonth selected={selectedWeekOrMonth} handleChangeMonthOrWeek={handleChangeMonthOrWeek} />
        {downloadButtonFeatureEnabled && (
          <FlexButton
            size="small"
            handleClick={handleGenerateReport}
            disabled={isProcessing}
            text={isProcessing ? 'Baixando relatório' : 'Baixar relatório'}
            endIcon={isProcessing ? AutorenewIconStyles : FileDownloadOutlinedIcon}
          />
        )}
      </RightContainer>
    </Content>
  );
};
